import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';

import {
  Access_ActionAccess_Fragment,
  Access_Answer_Fragment,
} from '../fragments/access-answer.fragment';
import { gateOf, stateChangeGateOf } from '../utils/access-gate.util';
import type { AccessGate } from '../types/access-gate.type';

import { useRefetchOnChange } from './useRefetchOnChange.hook';

export const FolderAccess_QueryDocument = graphql(`
  query FolderAccess_Query($id: Id!) {
    findOneFolder(id: $id) {
      _id
      access {
        update: can(scope: "update") {
          ...Access_Answer_Fragment
        }
        initMission: can(scope: "initMission") {
          ...Access_Answer_Fragment
        }
        initBusinessEvent: can(scope: "initBusinessEvent") {
          ...Access_Answer_Fragment
        }
        actions {
          ...Access_ActionAccess_Fragment
        }
      }
    }
  }
`);

export interface FolderAccess {
  // When the answers were received (0 before): a page decides on answers received after it opened.
  readonly answeredAt: number;
  readonly update: AccessGate;
  readonly initMission: AccessGate;
  readonly initBusinessEvent: AccessGate;
  readonly changeState: AccessGate;
}

// What the user may do on a folder; `version` is the folder as loaded, so that a write refreshes it.
export const useFolderAccess = (
  folderId: Id | undefined,
  version?: unknown,
): FolderAccess => {
  const { data, refetch, dataUpdatedAt } = useGraphQLQuery(
    FolderAccess_QueryDocument,
    { id: folderId ?? EMPTY_ID },
    { enabled: !!folderId },
  );

  useRefetchOnChange(refetch, version);

  const access = data?.findOneFolder.access;

  return {
    answeredAt: dataUpdatedAt,
    update: gateOf(getFragmentData(Access_Answer_Fragment, access?.update)),
    initMission: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initMission),
    ),
    initBusinessEvent: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initBusinessEvent),
    ),
    changeState: stateChangeGateOf(
      getFragmentData(Access_ActionAccess_Fragment, access?.actions),
    ),
  };
};
